import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import React, { useCallback } from 'react'
import { useController } from 'react-hook-form'

import { isRawMobileCode } from 'utils/exceptionFields'

import type { ExceptionFormValues } from './constants'
import type { Control } from 'react-hook-form'

// One standalone button per option (not a connected/segmented group), single-select.
type ToggleOptionProps = {
  option: string
  selected: boolean
  onSelect: (value: string) => void
}

const ToggleOption: React.FC<ToggleOptionProps> = ({ option, selected, onSelect }) => {
  const handleClick = useCallback(() => onSelect(option), [option, onSelect])

  return (
    <Button
      color={selected ? 'primary' : 'inherit'}
      size="small"
      sx={{ flex: 1 }}
      variant={selected ? 'contained' : 'outlined'}
      onClick={handleClick}
    >
      {option}
    </Button>
  )
}

// RHF-bound single-select rendered as separate buttons. Used by the Damage damage-type /
// responsible-party selectors and the No Access sub-option.
type ExceptionToggleFieldProps = {
  control: Control<ExceptionFormValues>
  name: keyof ExceptionFormValues
  options: string[]
  label?: string
}

const ExceptionToggleField: React.FC<ExceptionToggleFieldProps> = ({ control, name, options, label }) => {
  const {
    field: { value, onChange },
  } = useController({ control, name })

  // Mobile-logged exceptions carry free text ("wheels") that isn't in the web option set —
  // append the stored value as its own (selected) option so the captured data stays visible
  // instead of rendering an empty group. Raw numeric dictionary codes ("1", "101") are NOT
  // valid options — they stay hidden (but preserved in form state so saves don't strip them)
  // until the mobile code→label map lands.
  const storedValue = typeof value === 'string' ? value : ''
  const displayOptions =
    storedValue && !options.includes(storedValue) && !isRawMobileCode(storedValue) ? [...options, storedValue] : options

  return (
    <Box>
      {label && (
        <Typography color="textSecondary" mb={0.5} variant="body2">
          {label}
        </Typography>
      )}
      <Stack direction="row" gap={1}>
        {displayOptions.map(option => (
          <ToggleOption key={option} option={option} selected={value === option} onSelect={onChange} />
        ))}
      </Stack>
    </Box>
  )
}

export default ExceptionToggleField
